import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Stack, TextField, PrimaryButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Dropdown, IDropdownOption, Label } from "@fluentui/react";
import { getModifyUserData, modifyUser } from "../../../api/admin/api_admin";
import { ModifyUserResponse, Group } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";
import { validateAuthorityLevel, validateUsernameAlphabet } from "../utils";

export const ModifyUser = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user_id');
    
    const [data, setData] = useState<ModifyUserResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [username, setUsername] = useState<string>("");
    const [usernameAlphabet, setUsernameAlphabet] = useState<string>("");
    const [authorityLevel, setAuthorityLevel] = useState<string>("");
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [isAuthorityLevelDisabled, setIsAuthorityLevelDisabled] = useState<boolean>(false);

    useEffect(() => {
        if (userId) {
            fetchData(userId);
        }
    }, [userId]);

    const fetchData = async (uid: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getModifyUserData(uid);
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
                setUsername(result.user.USR_username);
                setUsernameAlphabet(result.user.USR_username_alphabet || "");
                setAuthorityLevel(result.user.authority_level.toString());
                setSelectedGroups(result.user_groups || []);
                setIsAuthorityLevelDisabled(result.user.authority_level === 10);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleGroupChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        if (option) {
            setSelectedGroups(
                option.selected ? [...selectedGroups, option.key as string] : selectedGroups.filter(key => key !== option.key)
            );
        }
    };

    const handleSubmit = async () => {
        if (!userId) return;

        setSubmitting(true);
        setError(null);

        // バリデーション
        if (!username) {
            setError("ユーザー名は必須です");
            setSubmitting(false);
            return;
        }
        if (data?.is_strict_pswd_policy && !usernameAlphabet) {
            setError("姓名(ローマ字)は必須です");
            setSubmitting(false);
            return;
        }
        if (usernameAlphabet && !validateUsernameAlphabet(usernameAlphabet)) {
            setError("姓名(ローマ字)は半角英数字（大文字・小文字）と半角スペースのみ使用できます");
            setSubmitting(false);
            return;
        }
        if (!authorityLevel) {
            setError("権限レベルは必須です");
            setSubmitting(false);
            return;
        }
        if (!validateAuthorityLevel(authorityLevel)) {
            setError("権限レベルは一桁の半角数字(1-9)のみ使用できます");
            setSubmitting(false);
            return;
        }

        try {
            const result = await modifyUser({
                userid: userId,
                username,
                username_alphabet: usernameAlphabet,
                authority_level: authorityLevel,
                groups: selectedGroups
            });

            if (result.success) {
                navigate("/menu/users");
            } else {
                setError(result.message || "更新に失敗しました");
            }
        } catch (err) {
            setError("更新中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const getGroupOptions = (): IDropdownOption[] => {
        if (!data) return [];
        return data.group_list.map((group: Group) => ({
            key: group.group_cd,
            text: `${group.group_cd} - ${group.group_name}`
        }));
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    if (!userId || !data) {
        return (
            <Stack tokens={{ childrenGap: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    {error ?? "ユーザーIDが指定されていません"}
                </MessageBar>
                <LinkButton link_to="/menu/users" text="戻る" />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="ユーザー情報変更" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <Stack>
                    <Label>ユーザーID</Label>
                    <div>{userId}</div>
                </Stack>

                <TextField
                    name="username"
                    label="ユーザー名"
                    value={username}
                    onChange={(_, newValue) => setUsername(newValue || "")}
                    required
                    maxLength={100}
                    description="※100字以下で入力してください。"
                    autoComplete="off"
                />

                <TextField
                    name="username_alphabet"
                    label="姓名(ローマ字)"
                    value={usernameAlphabet}
                    onChange={(_, newValue) => setUsernameAlphabet(newValue || "")}
                    required={data?.is_strict_pswd_policy}
                    maxLength={100}
                    description="※使用できる文字は、半角英数字（大文字・小文字）と半角スペースのみです。"
                    autoComplete="off"
                />

                <TextField
                    name="authority_level"
                    label="権限レベル"
                    value={authorityLevel}
                    onChange={(_, newValue) => !isAuthorityLevelDisabled && setAuthorityLevel(newValue || "")}
                    required
                    maxLength={1}
                    description="※使用できる文字は一桁の半角数字のみです。"
                    disabled={isAuthorityLevelDisabled}
                    autoComplete="off"
                />

                <Dropdown
                    label="グループ"
                    placeholder="グループを選択"
                    multiSelect
                    options={getGroupOptions()}
                    selectedKeys={selectedGroups}
                    onChange={handleGroupChange}
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="登録"
                        onClick={handleSubmit}
                        disabled={submitting || !username || !authorityLevel}
                    />
                    <LinkButton link_to="/menu/users" text="戻る" />

                    <LinkButton 
                    link_to={`/menu/users/reset_password?user_id=${userId}`}
                    text="パスワード変更"
                />
                </Stack>
            </Stack>
        </Stack>
    );
}